<script>
  import { onMount } from "svelte"

  export let siteKey
  export let authToken
  export let honeypot

  let data = {
    authenticity_token: authToken,
    benefit_who: "",
    cf_turnstile_response: "",
    email: "",
    fall_detection: "",
    first_name: "",
    kind_of_protection: "",
    last_name: "",
    lead_source: "Email Lead",
    phone: "",
    referral_source: "",
    utm_campaign: "",
    utm_content: "",
    utm_medium: "",
    utm_source: "",
  }

  let currentStep = 1
  let totalSteps = 4
  let formSubmitted = false
  let formSuccess = false
  let formSending = false
  let errors = []
  let formElement

  let partnerPhone = "800-331-9198"

  function checkPartnerDataReady() {
    if (window.partnerDataReady && window.partnerData) {
      partnerPhone = window.partnerData?.phone ?? "800-331-9198"
    } else {
      setTimeout(checkPartnerDataReady, 100) // Check again after 100ms
    }
  }

  function checkUtmDataReady() {
    if (window.utmDataReady && window.utmData) {
      data.utm_campaign = window.utmData?.utm_campaign ?? ""
      data.utm_content = window.utmData?.utm_content ?? ""
      data.utm_medium = window.utmData?.utm_medium ?? ""
      data.utm_source = window.utmData?.utm_source ?? ""
    } else {
      setTimeout(checkUtmDataReady, 100) // Check again after 100ms
    }
  }

  function setReferralSource() {
    const currentUrl = new URL(window.location.href)
    currentUrl.search = ""
    data.referral_source = currentUrl.toString()
  }

  function renderTurnstile() {
    if (!formElement.dataset.turnstileInitialized) {
      window.turnstile.render("#partnersQuoteFormTurnstile", {
        sitekey: siteKey,
        callback: (token) => {
          data.cf_turnstile_response = token
        },
      })
    }
  }

  function waitForTurnstile() {
    window.turnstile ? renderTurnstile() : setTimeout(waitForTurnstile, 100)
  }

  onMount(() => {
    waitForTurnstile()
    checkPartnerDataReady()
    checkUtmDataReady()
    setReferralSource()
  })

  // Function to handle form step navigation
  function handleAction(e) {
    const dataField = e.target.getAttribute("data-field")
    const dataValue = e.target.getAttribute("data-value")

    if (dataField) {
      data[dataField] = dataValue
    }

    currentStep += 1
  }

  // Function to handle form submission
  async function handleSubmit() {
    formSending = true

    // Append any fields we aren't already accounting for
    const fields = formElement.elements
    for (let field of fields) {
      if (field.name && !data[field.name]) {
        data[field.name] = field.value;
      }
    }

    // Send the form data via fetch
    try {
      const response = await fetch("/forms/salesforce_api", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
            .content,
        },
        body: JSON.stringify(data),
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      formSubmitted = true
      formSuccess = true
    } catch (error) {
      console.error("Error:", error)
      formSubmitted = true
      formSuccess = false
      errors.push(error.message)
    } finally {
      formSending = false
    }
  }

  onMount(() => {
    if (window.partnerData && window.partnerData.phone) {
      partnerPhone = window.partnerData.phone
    }
    if (window.utmData) {
      for (const [key, value] of Object.entries(window.utmData)) {
        if (data[key]) {
          data[key] = value
        }
      }
    }
  })
</script>

<form
  id="get_quote"
  class="get-quote !bg-[rgb(219,226,233)]"
  on:submit|preventDefault={handleSubmit}
  bind:this={formElement}
>
  <div class="wrapper">
    <div class="heading !bg-[rgba(58,118,173,1)] !text-xl">
      Get Your Fast, <span class="free font-black">Free</span> and Easy Quote
    </div>
    <div class="slides">
      {#if currentStep < totalSteps}
        <div
          class="step !text-black mt-6 mb-5 uppercase text-lg font-medium leading-none"
        >
          Step {currentStep} of {totalSteps}
        </div>
      {/if}
      {#if !formSubmitted && currentStep === 1}
        <div class="form-step">
          <div class="contents">
            <p>Who can benefit from having a LifeFone service?</p>
            <button
              class="action text-white bg-[rgba(14,61,108,1)] border-0"
              data-field="benefit_who"
              data-value="Self"
              on:click={handleAction}
            >
              Myself
            </button>
            <button
              class="action text-white bg-[rgba(14,61,108,1)] border-0"
              data-field="benefit_who"
              data-value="Other"
              on:click={handleAction}
            >
              A loved one
            </button>
          </div>
        </div>
      {/if}
      {#if !formSubmitted && currentStep === 2}
        <div class="form-step">
          <div class="contents text-align-center">
            <p>What kind of protection are you looking for?</p>
            <button
              class="action text-white bg-[rgba(14,61,108,1)] border-0"
              data-field="kind_of_protection"
              data-value="At Home Only"
              on:click={handleAction}
            >
              At Home
            </button>
            <button
              class="action text-white bg-[rgba(14,61,108,1)] border-0"
              data-field="kind_of_protection"
              data-value="On-The-Go"
              on:click={handleAction}
            >
              On-The-Go
            </button>
            <button
              class="action text-white bg-[rgba(14,61,108,1)] border-0 !mx-auto !float-none"
              data-field="kind_of_protection"
              data-value="At Home &amp; On-The-Go"
              on:click={handleAction}
            >
              Both
            </button>
          </div>
        </div>
      {/if}
      {#if !formSubmitted && currentStep === 3}
        <div class="form-step">
          <div class="contents">
            <p>Are you looking for fall detection?</p>
            <button
              class="action text-white bg-[rgba(14,61,108,1)] border-0"
              data-field="fall_detection"
              data-value="Yes"
              on:click={handleAction}
            >
              Yes
            </button>
            <button
              class="action text-white bg-[rgba(14,61,108,1)] border-0"
              data-field="fall_detection"
              data-value="No"
              on:click={handleAction}
            >
              No
            </button>
          </div>
        </div>
      {/if}
      {#if !formSubmitted && currentStep === 4}
        <div class="form-step">
          <div class="contents">
            <div class="form">
              <p>A LifeFone specialist will contact you soon!</p>
              <input
                id="first_name"
                type="text"
                bind:value={data.first_name}
                placeholder="First Name"
                data-field="first_name"
                class="required-entry validate-alphanum-with-spaces"
                required
              />
              <input
                id="last_name"
                type="text"
                bind:value={data.last_name}
                placeholder="Last Name"
                data-field="last_name"
                class="required-entry validate-alphanum-with-spaces"
                required
              />
              <input
                id="email"
                type="email"
                bind:value={data.email}
                placeholder="Email"
                data-field="email"
                class="required-entry validate-email"
              />

              <input
                type="tel"
                bind:value={data.phone}
                placeholder="Telephone"
                data-field="phone"
                class="required-entry validate-phoneLax"
                required
              />

              {@html JSON.parse(honeypot)}

              <div class="required">* All Fields Required</div>
              <button
                type="submit"
                title="submit"
                class="submit uk-button uk-button-primary bg-[rgba(14,61,108,1)] border-0"
                disabled={formSending}
              >
                {#if formSending}
                  <svg
                    aria-hidden="true"
                    role="status"
                    class="inline w-4 h-4 me-3 text-white animate-spin"
                    viewBox="0 0 100 101"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                      fill="#E5E7EB"
                    />
                    <path
                      d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                      fill="currentColor"
                    />
                  </svg>
                  Sending
                {:else}
                  Submit
                {/if}
              </button>
            </div>
          </div>
        </div>
      {/if}
      {#if formSubmitted && formSuccess}
        <div class="success-submit max-w-[370px] mx-auto py-4">
          <div class="mx-auto my-0">
            <p class="text-2xl !mb-2.5 text-gray-700">
              <strong
                >The additional information you requested is on the way.</strong
              >
            </p>
            <p class="text-lg">
              Our Customer Care Representatives are here 7 days a week to answer
              any questions you may have and can help you subscribe to
              LifeFone's life-saving service.
            </p>
            <p class="text-[#0e3d6d] font-extrabold">
              Call today <span class="phone-replace-success"
                >{partnerPhone}</span
              >
            </p>
            <a href="/order-now" class="uk-button uk-text-uppercase"
              >Order Now <i
                class="uk-icon uk-icon-caret-right uk-margin-small-left"
              ></i></a
            >
          </div>
        </div>
      {/if}
      {#if formSubmitted && !formSuccess}
        <div class="error-submit">
          <p class="text-2xl !mb-2.5">
            <strong>There was an error submitting the form.</strong>
          </p>
          <p class="text-lg">
            Please try again or call us at
            <span class="phone-replace-success">{partnerPhone}</span> to speak with
            a Customer Care Representative.
          </p>
        </div>
      {/if}
      {#if errors.length > 0}
        <div class="errors">
          <ul>
            {#each errors as error}
              <li>{error}</li>
            {/each}
          </ul>
        </div>
      {/if}
      {#if !formSubmitted}
        <div class="flex justify-center mt-2 w-full">
          <div
            class={currentStep !== totalSteps ? "hidden" : ""}
            data-action="salesforce_api"
            data-appearance="always"
            data-sitekey={siteKey}
            data-theme="light"
            id="partnersQuoteFormTurnstile"
          ></div>
        </div>
      {/if}
    </div>
  </div>
</form>
